// /web/src/pages/calculators/WC.jsx
import React from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useApi } from "../../lib/api";
import { trackAmplitudeEvent, trackCalculatorRun } from "../../lib/amplitude";
import toast from "react-hot-toast";
import CalculatorLayout, {
  CalculatorSection,
} from "../../components/calculators/CalculatorLayout.jsx";
import DownloadWCReportBtn from "../../components/pdf/DownloadWCReportBtn.jsx";

function toErrorMessage(err) {
  if (!err) return "Unknown error";
  if (typeof err === "string") return err;
  if (err.detail) {
    if (typeof err.detail === "string") return err.detail;
    if (Array.isArray(err.detail) && err.detail[0]?.msg) return err.detail[0].msg;
  }
  if (typeof err.message === "string" && err.message !== "[object Object]") return err.message;
  try { return JSON.stringify(err); } catch { return String(err); }
}

// ----- formatting helpers -----
function to0(x) {
  const n = Number(x);
  if (!isFinite(n)) return "0";
  return Math.round(n).toString();
}
function to2(x) {
  const n = Number(x);
  if (!isFinite(n)) return "0.00";
  return n.toFixed(2);
}

const ORD_WORDS = [
  "First","Second","Third","Fourth","Fifth","Sixth","Seventh","Eighth","Ninth","Tenth",
  "Eleventh","Twelfth","Thirteenth","Fourteenth","Fifteenth","Sixteenth","Seventeenth",
  "Eighteenth","Nineteenth","Twentieth"
];

const ACCESSIBLE_DIMENSIONS = {
  width_m: 1.5,
  length_m: 2.2,
  area_m2: 1.5 * 2.2,
};

const WC_CALCULATOR_META = {
  title: "Sanitary Provision Calculator",
  subtitle: "Results compliant with Part T and Part M.",
  notes: [
    "We size fixtures using BS 6465-1 best practice rates with Workplace Regulation fallbacks, and Part M parameters for accessible rooms.",
    "Review the assumptions before issuing schedules or sharing externally.",
    "Part T still expects gender-separated toilets in most workplaces unless specifically justified.",
  ],
  checklist: [
    "Provide at least one unisex wheelchair-accessible WC on every floor served and keep it on an accessible route.",
    "Keep travel distances to the accessible WC within the typical 40 m guidance.",
    "Include the required transfer space, outward-opening door, and emergency assistance alarm to meet Part M and BS 8300.",
  ],
};

/**
 * Floor naming based on DB indices:
 * entranceIdx is the floor_index that is Ground.
 * offset 0 -> Ground Floor
 * offset -1 -> Basement, -2 -> Basement 2, ...
 * offset +1 -> First Floor, +2 -> Second Floor, ...
 */
function storeyName(floorIndex, entranceIdx) {
  if (entranceIdx == null) return `Floor ${floorIndex + 1}`;
  const offset = floorIndex - entranceIdx;
  if (offset === 0) return "Ground Floor";
  if (offset < 0) {
    const n = Math.abs(offset);
    return n === 1 ? "Basement" : `Basement ${n}`;
  }
  if (offset <= ORD_WORDS.length) return `${ORD_WORDS[offset - 1]} Floor`;
  return `${offset}th Floor`;
}

/**
 * Component-driven footprint (PLAN):
 * width_mm = 700 (basin) + 1500 (circ) + 1750 (toilet) + 300 (services) + wall_mm
 * wall_mm = 125 if layout==='washrooms' else 25
 * length_mm =
 *   (std_count*910 + amb_count*920 + (n_wcs-1)*wall_mm)       // cubicles run
 *   + (both? wall_mm : 0)                                     // separator between cubicles & urinals
 *   + (n_urinals*800 + max(n_urinals-1,0)*wall_mm)            // urinals run
 * returns { width_m, length_m, area_m2, parts }
 */
function computeFootprint(n_wcs, n_urinals, layout) {
  const wall_mm = layout === "washrooms" ? 125 : 25;
  const width_mm = 700 + 1500 + 1750 + 300 + wall_mm; // 4375 or 4275

  const amb_count = n_wcs > 0 ? 1 : 0;
  const std_count = Math.max(n_wcs - amb_count, 0);
  const cub_sep = Math.max(n_wcs - 1, 0);

  const cub_len_mm = std_count * 910 + amb_count * 920 + cub_sep * wall_mm;

  const ur_sep = Math.max(n_urinals - 1, 0);
  const uri_len_mm = n_urinals * 800 + ur_sep * wall_mm;  // urinals = 800 mm each

  const between_mm = (n_wcs > 0 && n_urinals > 0) ? wall_mm : 0;

  const length_mm = cub_len_mm + between_mm + uri_len_mm;

  const width_m = width_mm / 1000;
  const length_m = length_mm / 1000;
  const area_m2 = width_m * length_m;

  return {
    width_m,
    length_m,
    area_m2,
    parts: {
      wall_mm,
      width_mm,
      cub: { std_count, amb_count, sep: cub_sep, len_mm: cub_len_mm },
      uri: { count: n_urinals, sep: ur_sep, len_mm: uri_len_mm },
      between_mm,
    }
  };
}

export default function WCCalculator() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const api = useApi();
  const rawUsageEventId = searchParams.get("usage_event_id");
  const usageEventId = rawUsageEventId ? rawUsageEventId.trim() : "";
  const usageEventPath = usageEventId
    ? `/usage-events/${encodeURIComponent(usageEventId)}`
    : "";

  const effectiveId =
    params.id ||
    searchParams.get("project_id") ||
    searchParams.get("projectId") ||
    searchParams.get("project") ||
    null;

  const [project, setProject] = React.useState(null);
  const [loading, setLoading] = React.useState(!!effectiveId);
  const [error, setError] = React.useState("");
  const [historyEvent, setHistoryEvent] = React.useState(null);
  const [historyLoading, setHistoryLoading] = React.useState(false);
  const [historyError, setHistoryError] = React.useState("");

  // Settings state
  const [standard, setStandard] = React.useState("bs6465-1"); // or "workplace-min"
  const [utilPercent, setUtilPercent] = React.useState(100);
  const [genderSplit, setGenderSplit] = React.useState("60/60");
  const [includeUrinals, setIncludeUrinals] = React.useState(false);
  const [includeAccessible, setIncludeAccessible] = React.useState(true);
  const [layoutType, setLayoutType] = React.useState("cubicles"); // cubicles|washrooms
  const [occRatio, setOccRatio] = React.useState(10); // m²/person fallback

  // Results
  const [results, setResults] = React.useState(null);
  const [running, setRunning] = React.useState(false);
  const [calcErr, setCalcErr] = React.useState("");
  const [activeTab, setActiveTab] = React.useState("cubicles"); // "cubicles" | "self-contained"

  // Entrance floor index (DB floor_index that is Ground)
  const [entranceIdx, setEntranceIdx] = React.useState(null);

  // Project picker state when no project selected yet
  const [projects, setProjects] = React.useState([]);
  const [loadingProjects, setLoadingProjects] = React.useState(!effectiveId);
  const [pickErr, setPickErr] = React.useState("");

  // Load either a specific project (and its inputs) or the list of projects for selection
  React.useEffect(() => {
    let active = true;

    async function loadProjectAndInputs(pid) {
      try {
        setLoading(true);
        setError("");
        setCalcErr("");
        setResults(null);

        const [p, rows] = await Promise.all([
          api(`/projects/${pid}`),
          api(`/projects/${pid}/inputs`),
        ]);

        if (!active) return;

        setProject(p || null);

        const occ = p?.occupancy_ratio != null ? Number(p.occupancy_ratio) : null;
        if (Number.isFinite(occ)) {
          setOccRatio(occ);
        }

        if (Array.isArray(rows) && rows.length) {
          const hit = rows.find((r) => r.is_entrance_floor);
          if (hit && typeof hit.floor_index === "number") {
            setEntranceIdx(hit.floor_index);
          } else {
            setEntranceIdx(0);
          }
        } else {
          setEntranceIdx(0);
        }
      } catch (e) {
        if (!active) return;
        setError(toErrorMessage(e) || "Failed to load project");
        setEntranceIdx(0);
      } finally {
        if (active) setLoading(false);
      }
    }

    async function loadProjectsList() {
      try {
        setLoadingProjects(true);
        setPickErr("");
        const list = await api(`/projects`);
        if (!active) return;
        setProjects(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!active) return;
        setPickErr(toErrorMessage(e) || "Failed to load projects");
      } finally {
        if (active) setLoadingProjects(false);
      }
    }

    if (effectiveId) {
      setLoadingProjects(false);
      setPickErr("");
      setProjects([]);
      loadProjectAndInputs(effectiveId);
    } else if (!usageEventId) {
      setProject(null);
      setEntranceIdx(null);
      setError("");
      setCalcErr("");
      setResults(null);
      setLoading(false);
      loadProjectsList();
    } else {
      setProject(null);
      setEntranceIdx(null);
      setError("");
      setLoading(false);
      setProjects([]);
      setLoadingProjects(false);
    }

    return () => {
      active = false;
    };
  }, [effectiveId, api, usageEventId]);

  React.useEffect(() => {
    let active = true;
    if (!usageEventPath) {
      setHistoryEvent(null);
      setHistoryLoading(false);
      setHistoryError("");
      return () => {
        active = false;
      };
    }

    async function loadHistory() {
      try {
        setHistoryLoading(true);
        setHistoryError("");
        const data = await api(usageEventPath);
        if (!active) return;
        setHistoryEvent(data || null);
      } catch (err) {
        if (!active) return;
        setHistoryEvent(null);
        const msg = toErrorMessage(err);
        setHistoryError(
          usageEventId ? `${msg} (usage_event_id=${usageEventId})` : msg
        );
        console.error(
          "[WC] Failed to load saved usage_event",
          usageEventId,
          err
        );
        trackAmplitudeEvent("usage_event_fetch_failed", {
          calc_type: "wc",
          usage_event_id: usageEventId || null,
          message: msg,
        });
      } finally {
        if (active) setHistoryLoading(false);
      }
    }

    loadHistory();
    return () => {
      active = false;
    };
  }, [api, usageEventId, usageEventPath]);

  React.useEffect(() => {
    if (!historyEvent?.details) return;
    const details = historyEvent.details;
    if (details.standard) setStandard(details.standard);
    const util = Number(details.utilisation_percent);
    if (Number.isFinite(util)) setUtilPercent(util);
    if (details.gender_split) setGenderSplit(details.gender_split);
    if (typeof details.include_urinals === "boolean")
      setIncludeUrinals(details.include_urinals);
    else if (details.provision_mode)
      setIncludeUrinals(details.provision_mode === "cubicles_urinals");
    if (typeof details.include_accessible === "boolean")
      setIncludeAccessible(details.include_accessible);
    if (details.layout_type) setLayoutType(details.layout_type);
    const occ = Number(details.occupancy_ratio_sqm_per_person);
    if (Number.isFinite(occ) && occ > 0) setOccRatio(occ);
  }, [historyEvent]);

  React.useEffect(() => {
    if (!historyEvent?.result_json) return;
    setResults(historyEvent.result_json);
    setActiveTab("cubicles");
  }, [historyEvent]);

  async function onRun() {
    setRunning(true);
    setResults(null);
    setCalcErr("");

    try {
      const payload = {
        standard,
        utilisation_percent: Number(utilPercent),
        gender_split: genderSplit,
        include_urinals: includeUrinals,
        include_accessible: includeAccessible,
        layout_type: layoutType,
        occupancy_ratio_sqm_per_person: Number(occRatio),
      };

      let url = "/calc/wc/run";
      if (effectiveId) url += `?project_id=${encodeURIComponent(effectiveId)}`;

      const resp = await api(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const totals = Array.isArray(resp?.results)
        ? resp.results.reduce(
            (acc, row) => {
              const accessibleCount = includeAccessible
                ? (typeof row.accessible_wcs === "number"
                    ? row.accessible_wcs
                    : row.persons > 0
                      ? 1
                      : 0)
                : 0;
              return {
                floors: acc.floors + 1,
                persons: acc.persons + (Number(row.persons) || 0),
                male_wcs: acc.male_wcs + (Number(row.male_wcs) || 0),
                female_wcs: acc.female_wcs + (Number(row.female_wcs) || 0),
                accessible_wcs: acc.accessible_wcs + accessibleCount,
                urinals: acc.urinals + (Number(row.urinals) || 0),
              };
            },
            { floors: 0, persons: 0, male_wcs: 0, female_wcs: 0, accessible_wcs: 0, urinals: 0 },
          )
        : null;

      trackCalculatorRun("wc", {
        projectId: effectiveId,
        inputs: payload,
        resultSummary: totals
          ? {
              floors: totals.floors,
              total_persons: totals.persons,
              male_wcs: totals.male_wcs,
              female_wcs: totals.female_wcs,
              accessible_wcs: totals.accessible_wcs,
              urinals: totals.urinals,
              standard: resp?.standard_applied,
              provision_mode: resp?.provision_mode,
            }
          : {
              standard: resp?.standard_applied,
              provision_mode: resp?.provision_mode,
            },
      });

      setResults(resp);
      setActiveTab("cubicles");
      toast.success("Calculation complete");
    } catch (e) {
      const msg = toErrorMessage(e);
      toast.error(msg || "Calculation failed");
      setCalcErr(msg || "Calculation failed");
    } finally {
      setRunning(false);
    }
  }

  const hasResults = !!(results && Array.isArray(results.results) && results.results.length > 0);
  const hasUrinals = results?.provision_mode === "cubicles_urinals";
  const showAccessible = includeAccessible;
  const showPicker = !effectiveId && !usageEventId;
  const projectSummary = project
    ? [
        project.name,
        project.postcode,
        project.local_authority,
        project.floors_count ? `${project.floors_count} floors` : null,
      ].filter(Boolean)
    : null;

  const selfContainedRows = React.useMemo(() => {
    if (!hasResults || !results || !Array.isArray(results.results)) return null;

    const uplift = (value) => {
      const n = Number(value) || 0;
      if (n <= 0) return 0;
      return n + Math.ceil(n / 4);
    };

    const scale = (base, from, to) => {
      const b = Number(base) || 0;
      const f = Number(from) || 0;
      if (!b || !f) return b;
      return (b * to) / f;
    };

    return results.results.map((row) => {
      const maleBase = Number(row.male_wcs) || 0;
      const femaleBase = Number(row.female_wcs) || 0;

      const maleWc = uplift(maleBase);
      const femaleWc = uplift(femaleBase);

      const maleArea = scale(row.male_area_m2, maleBase, maleWc);
      const femaleArea = scale(row.female_area_m2, femaleBase, femaleWc);

      return {
        ...row,
        male_wcs: maleWc,
        female_wcs: femaleWc,
        male_basins: 0,
        female_basins: 0,
        male_area_m2: maleArea,
        female_area_m2: femaleArea,
        urinals: 0,
      };
    });
  }, [hasResults, results]);

  const rowsForView =
    activeTab === "self-contained" && selfContainedRows && selfContainedRows.length
      ? selfContainedRows
      : results?.results || [];

  const showUrinals = activeTab === "self-contained" ? false : hasUrinals;

  // Find largest floor by NIA and its index in the original results
  let largest = null;
  let largestIdx = -1;
  if (hasResults) {
    results.results.forEach((r, i) => {
      if (!largest || Number(r.nia_m2) > Number(largest.nia_m2)) {
        largest = r;
        largestIdx = i; // equals DB floor_index due to ASC order from server
      }
    });
  }

  const largestRowForView =
    activeTab === "self-contained" && selfContainedRows && largestIdx >= 0
      ? selfContainedRows[largestIdx]
      : largest;

  return (
    <CalculatorLayout
      title={WC_CALCULATOR_META.title}
      subtitle={WC_CALCULATOR_META.subtitle}
      project={project}
      projectDetails={projectSummary}
      costLabel="Included with subscription"
      onRun={showPicker ? null : onRun}
      runLabel="Run calculation"
      runDisabled={showPicker || running || loading || historyLoading}
      runLoading={running}
    >
      {usageEventId && (
        <CalculatorSection title="Reopened run">
          {historyLoading && <p>Loading saved inputs…</p>}
          {!historyLoading && historyEvent && (
            <p>
              Restored calculator inputs from {(() => {
                const parsed = new Date(historyEvent.created_at);
                return Number.isNaN(parsed.valueOf())
                  ? historyEvent.created_at
                  : parsed.toLocaleString();
              })()}.
            </p>
          )}
          {!historyLoading && historyError && (
            <p className="pd-error">Failed to load saved run: {historyError}</p>
          )}
        </CalculatorSection>
      )}
      {showPicker ? (
        <CalculatorSection title="Select a project" description="Pick a project to run the WC calculator.">
          {pickErr && (
            <div className="pd-alert">
              <strong>Error:</strong> {pickErr}
            </div>
          )}
          {loadingProjects ? (
            <div className="pd-results-placeholder pd-results-placeholder--auto">Loading your projects…</div>
          ) : projects.length === 0 ? (
            <div className="pd-empty-state">
              <p>You don’t have any projects yet.</p>
              <div className="pd-empty-state__actions">
                <Link to="/projects/new" className="pd-btn pd-btn--primary">
                  + Create new project
                </Link>
              </div>
            </div>
          ) : (
            <div className="pd-project-grid">
              {projects.map((p) => (
                <Link
                  key={p.id}
                  to={`/projects/${p.id}/calculators/wc`}
                  className="pd-project-card"
                >
                  <div className="pd-project-card__head">
                    <div>
                      <div className="pd-project-card__title">{p.name}</div>
                      <div className="pd-project-card__meta">
                        {p.postcode || "—"} · {p.local_authority || "—"}
                      </div>
                    </div>
                    <span className="pd-pill">{(p.building_use || "office").toUpperCase()}</span>
                  </div>
                  <div className="pd-project-card__foot">
                    Floors: {p.floors_count ?? "—"}
                    <span className="pd-project-card__cta">Open WC calculator →</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CalculatorSection>
      ) : (
        <>
          {loading && <div className="pd-caption">Loading project…</div>}
          {error && (
            <div className="pd-alert">
              <strong>Error:</strong> {error}
            </div>
          )}

          <CalculatorSection
            title="Input parameters"
            description="Configure occupancy, standards, and layout before running the calculation."
          >
            <div className="pd-input-grid">
              <div className="pd-form-group">
                <label htmlFor="wc-building-type">Building type</label>
                <input
                  id="wc-building-type"
                  type="text"
                  value={
                    project?.building_use
                      ? project.building_use[0].toUpperCase() + project.building_use.slice(1)
                      : "Office"
                  }
                  readOnly
                />
                <p className="pd-form-note">Set in project overview.</p>
              </div>

              <div className="pd-form-group">
                <label htmlFor="wc-occupancy">Occupancy density</label>
                <select
                  id="wc-occupancy"
                  value={String(occRatio)}
                  onChange={(e) => setOccRatio(Number(e.target.value))}
                >
                  <option value="6">1:6</option>
                  <option value="8">1:8</option>
                  <option value="10">1:10</option>
                  <option value="12.5">1:12.5</option>
                  <option value="15">1:15</option>
                </select>
                <p className="pd-form-note">People per workpoint (m²/person).</p>
              </div>

              <div className="pd-form-group pd-form-group--full">
                <label htmlFor="wc-standard">Relevant standards</label>
                <select
                  id="wc-standard"
                  value={standard}
                  onChange={(e) => setStandard(e.target.value)}
                >
                  <option value="bs6465-1">BS 6465-1 (best practice)</option>
                  <option value="workplace-min">Workplace Regulations (minimum)</option>
                </select>
              </div>

              <div className="pd-form-group">
                <label htmlFor="wc-utilisation">Utilisation factor</label>
                <select
                  id="wc-utilisation"
                  value={String(utilPercent)}
                  onChange={(e) => setUtilPercent(Number(e.target.value))}
                >
                  {[100, 90, 80, 70, 60, 50].map((v) => (
                    <option key={v} value={v}>{v}%</option>
                  ))}
                </select>
                <p className="pd-form-note">Adjusts simultaneous occupancy.</p>
              </div>

              <div className="pd-form-group">
                <label htmlFor="wc-gender-split">Gender split</label>
                <select
                  id="wc-gender-split"
                  value={genderSplit}
                  onChange={(e) => setGenderSplit(e.target.value)}
                >
                  <option value="60/60">60% male / 60% female (concurrent)</option>
                  <option value="50/50">50% male / 50% female</option>
                  <option value="60/40">60% male / 40% female</option>
                  <option value="40/60">40% male / 60% female</option>
                </select>
              </div>
            </div>

            <div className="pd-divider" aria-hidden="true" />

            <div className="pd-toggle-grid">
              <label className="pd-toggle">
                <input
                  type="checkbox"
                  checked={includeUrinals}
                  onChange={(e) => setIncludeUrinals(e.target.checked)}
                />
                <div>
                  <div className="pd-toggle__title">Allow urinals</div>
                  <p className="pd-toggle__meta">Include urinals alongside male WCs.</p>
                </div>
              </label>

              <label className="pd-toggle">
                <input
                  type="checkbox"
                  checked={includeAccessible}
                  onChange={(e) => setIncludeAccessible(e.target.checked)}
                />
                <div>
                  <div className="pd-toggle__title">Include accessible WC</div>
                  <p className="pd-toggle__meta">Adds a unisex wheelchair-accessible WC to each block.</p>
                </div>
              </label>
            </div>

            <div className="pd-divider" aria-hidden="true" />

            <div className="pd-subsection-head">
              <div>
                <div className="pd-h4">Partition separation</div>
                <p className="pd-section-description">
                  Choose lightweight 25 mm cubicle partitions or 125 mm plasterboard walls between rooms.
                </p>
              </div>
            </div>

            <div className="pd-radio-grid">
              <label className={`pd-radio-card${layoutType === "cubicles" ? " is-selected" : ""}`}>
                <input
                  type="radio"
                  name="layout"
                  value="cubicles"
                  checked={layoutType === "cubicles"}
                  onChange={() => setLayoutType("cubicles")}
                />
                <div>
                  <div className="pd-radio-card__title">Cubicle partitions</div>
                  <div className="pd-radio-card__meta">25 mm separation</div>
                </div>
              </label>

              <label className={`pd-radio-card${layoutType === "washrooms" ? " is-selected" : ""}`}>
                <input
                  type="radio"
                  name="layout"
                  value="washrooms"
                  checked={layoutType === "washrooms"}
                  onChange={() => setLayoutType("washrooms")}
                />
                <div>
                  <div className="pd-radio-card__title">Plasterboard washrooms</div>
                  <div className="pd-radio-card__meta">125 mm separation</div>
                </div>
              </label>
            </div>
          </CalculatorSection>

          <CalculatorSection
            title="Notes"
            description="Key reminders from Part T, Part M, and BS 8300 guidance."
          >
            <div className="pd-calculator__notes">
              {WC_CALCULATOR_META.notes.map((note, idx) => (
                <p key={idx}>{note}</p>
              ))}
              <ul className="pd-notes-list">
                {WC_CALCULATOR_META.checklist.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </CalculatorSection>

          <CalculatorSection
            title="Results"
            description="Per-floor sanitary provision, fixture totals, and layout footprints."
            actions={
              <DownloadWCReportBtn
                project={project}
                results={results}
                selfContainedRows={selfContainedRows || []}
                includeAccessible={showAccessible}
                entranceIdx={entranceIdx}
                disabled={running || loading || historyLoading || !hasResults}
              />
            }
          >
            {calcErr && <div className="pd-alert">{calcErr}</div>}
            {hasResults ? (
              <>
                <div className="pd-tabs">
                  <button
                    type="button"
                    className={`pd-tab ${activeTab === "cubicles" ? "is-active" : ""}`}
                    onClick={() => setActiveTab("cubicles")}
                  >
                    Cubicles
                  </button>
                  <button
                    type="button"
                    className={`pd-tab ${activeTab === "self-contained" ? "is-active" : ""}`}
                    onClick={() => setActiveTab("self-contained")}
                  >
                    Self contained toilets
                  </button>
                </div>

                <section className="pd-results-card">
                  <h3 className="pd-h4">Per-floor breakdown</h3>
                  <div className="pd-results-card__meta">Standard: {results.standard_applied}</div>

                  <div className="pd-table-wrapper">
                    <table className="pd-table">
                      <thead>
                        <tr>
                          <th scope="col">Floor</th>
                          <th scope="col">NIA (m²)</th>
                          <th scope="col">Persons</th>
                          <th scope="col">Male WCs</th>
                          <th scope="col" className={!showUrinals ? "pd-table__header--muted" : undefined}>
                            Urinals
                          </th>
                          <th scope="col">Female WCs</th>
                          {showAccessible && <th scope="col">Unisex accessible WCs</th>}
                          <th scope="col">Male basins</th>
                          <th scope="col">Female basins</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rowsForView.map((r, i) => {
                          const accessibleCount =
                            typeof r.accessible_wcs === "number" ? r.accessible_wcs : r.persons > 0 ? 1 : 0;
                          return (
                            <tr key={`floor-${i}`}>
                              <th scope="row">{storeyName(r.floor_index ?? i, entranceIdx)}</th>
                              <td>{to0(r.nia_m2)}</td>
                              <td>{r.persons}</td>
                              <td>{r.male_wcs}</td>
                              <td className={!showUrinals ? "pd-table__cell--muted" : undefined}>
                                {showUrinals ? r.urinals : "—"}
                              </td>
                              <td>{r.female_wcs}</td>
                              {showAccessible && <td>{accessibleCount}</td>}
                              <td>{r.male_basins}</td>
                              <td>{r.female_basins}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {largestRowForView && (
                    <div className="pd-summary-card">
                      <div className="pd-summary-card__head">
                        <div>
                          <div className="pd-summary-card__title">Largest floor summary</div>
                          <div className="pd-summary-card__meta">
                            {storeyName(largestIdx, entranceIdx)} — NIA {to0(largestRowForView.nia_m2)} m²
                          </div>
                        </div>
                      </div>

                      {(() => {
                        const maleDims = computeFootprint(
                          largestRowForView.male_wcs,
                          showUrinals ? largestRowForView.urinals : 0,
                          layoutType
                        );
                        const femaleDims = computeFootprint(
                          largestRowForView.female_wcs,
                          0,
                          layoutType
                        );
                        const accessibleCount = showAccessible
                          ? (typeof largestRowForView.accessible_wcs === "number"
                              ? largestRowForView.accessible_wcs
                              : largestRowForView.persons > 0
                                ? 1
                                : 0)
                          : 0;
                        return (
                          <>
                            <div className="pd-summary-grid">
                              <div className="pd-summary-tile">
                                <div className="pd-summary-tile__title">Male provision</div>
                                <div className="pd-summary-tile__stat">WCs: {largestRowForView.male_wcs}</div>
                                {showUrinals && (
                                  <div className="pd-summary-tile__stat">Urinals: {largestRowForView.urinals}</div>
                                )}
                                <div className="pd-summary-tile__stat">Basins: {largestRowForView.male_basins}</div>
                                <div className="pd-summary-footprint">
                                  Footprint: {to2(maleDims.area_m2)} m² — {to2(maleDims.length_m)} m × {to2(maleDims.width_m)} m
                                </div>
                              </div>

                              <div className="pd-summary-tile">
                                <div className="pd-summary-tile__title">Female provision</div>
                                <div className="pd-summary-tile__stat">WCs: {largestRowForView.female_wcs}</div>
                                <div className="pd-summary-tile__stat">Basins: {largestRowForView.female_basins}</div>
                                <div className="pd-summary-footprint">
                                  Footprint: {to2(femaleDims.area_m2)} m² — {to2(femaleDims.length_m)} m × {to2(femaleDims.width_m)} m
                                </div>
                              </div>

                              {showAccessible && (
                                <div className="pd-summary-tile">
                                  <div className="pd-summary-tile__title">Unisex accessible</div>
                                  <div className="pd-summary-tile__stat">Toilets: {accessibleCount}</div>
                                  <div className="pd-summary-footprint">
                                    Part M minimum: {to2(ACCESSIBLE_DIMENSIONS.area_m2)} m² — {to2(ACCESSIBLE_DIMENSIONS.length_m)} m × {to2(ACCESSIBLE_DIMENSIONS.width_m)} m
                                  </div>
                                </div>
                              )}
                            </div>

                            <div className="pd-summary-note">
                              <p>
                                Areas and dimensions use fixed component sizes: basin depth 0.7 m, circulation 1.5 m,
                                toilet depth 1.75 m, services zone 0.3 m, plus wall thickness (0.125 m for washrooms,
                                0.025 m for cubicles). Cubicles are 0.91 m per standard WC and 0.92 m for one ambulant WC;
                                urinals are 0.80 m each. A separator at wall thickness is applied between adjacent runs,
                                and the accessible WC footprint uses the Part M minimum of 1.50 m × 2.20 m clear space.
                              </p>
                              <p className="pd-summary-note__meta">
                                Standard: {results.standard_applied}
                                {activeTab === "self-contained" && (
                                  <> — self contained WCs uplifted by 25% per BS 6465-1 clause 5.3.3.</>
                                )}
                              </p>
                              <ul className="pd-notes-list">
                                <li>
                                  Provide at least one unisex wheelchair-accessible WC on each floor and keep it on a level accessible route.
                                </li>
                                <li>
                                  Keep travel distances to the accessible WC within the typical 40 m guidance and provide clear signage (BS 8300-2:2018 clause 18).
                                </li>
                                <li>
                                  Include compliant transfer space, outward-opening door, and emergency assistance alarm.
                                </li>
                              </ul>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </section>
              </>
            ) : (
              <div className="pd-results-placeholder">Run the calculator to see sanitary provision.</div>
            )}
          </CalculatorSection>
        </>
      )}
    </CalculatorLayout>
  );
}
